import { ref } from 'vue';
import type { Market, MarketId, OutcomeParameterName, OutcomeParameterValue } from '../../types';
import type {
    OutcomeToDisplay,
    OutcomesByMarketId,
    FiltersByMarketId,
    FiltersByOutcomeParameterMap,
    OutcomesCtids,
} from './types';
import { sortFiltersValues } from './sortFiltersValues';
import { buildGroupKey } from './utils/buildGroupKey';
import { createFiltersByOutcomeParameterMap } from './utils/createFiltersByOutcomeParameterMap';

/**
 * Собирает исходы с корректным именем и агрегирует фильтры по параметрам исходов
 */
export function buildOutcomesWithFilters(
    markets: Market[],
    outcomesNamesByCtid: Map<number, string>,
): {
    outcomesByMarketId: OutcomesByMarketId;
    filtersByMarketId: FiltersByMarketId;
    // сохраняет существующие ctid исходов для каждого рынка (для последуюего добавления отсутствующих исходов)
    outcomesCtidsByMarketId: Map<MarketId, OutcomesCtids>;
    /*
     * Кросс-индекс для значений Total и Handicap
     * Используется, чтобы быстро отдавать фильтрам по тоталам и форам только те значения,
     * для которых при текущем выборе остальных фильтров (ROUND, PARTICIPANT и т.д.) есть хотя бы один outcome.
     */
    numericValuesIndexByMarketId: Map<MarketId, Map<string, Set<OutcomeParameterValue>>>;
} {
    const outcomesByMarketId: OutcomesByMarketId = new Map();
    const filtersByMarketId: FiltersByMarketId = new Map();
    const outcomesCtidsByMarketId: Map<MarketId, OutcomesCtids> = new Map();
    const numericValuesIndexByMarketId: Map<MarketId, Map<string, Set<OutcomeParameterValue>>> = new Map();

    for (const market of markets) {
        const outcomesToDisplay: OutcomeToDisplay[] = [];
        const filters: FiltersByOutcomeParameterMap = createFiltersByOutcomeParameterMap();
        const outcomesCtids: OutcomesCtids = new Set();
        const hasNumericValueParam = market.kind === 'Total' || market.kind === 'Handicap';
        const numericValuesByGroupKey: Map<string, Set<OutcomeParameterValue>> = new Map();

        for (const o of market.outcomes) {
            let outcomeName = outcomesNamesByCtid.get(o.ctid) ?? '';
            const paramsMap = new Map<OutcomeParameterName, Set<OutcomeParameterValue>>();
            outcomesCtids.add(o.ctid);

            // аккумулируем значение NUMERIC_PARAM_VALUE и пары name=value
            // остальных параметров для построения cross-индекса в этом же проходе
            let numericValueForIndex: OutcomeParameterValue | undefined;
            const groupingPairsForIndex: [OutcomeParameterName, OutcomeParameterValue][] = [];

            for (let i = 0; i < o.params.length; i++) {
                // пропускаем параметры без имени и значения
                if (o.params[i].name === '' && o.params[i].value === '') {
                    continue;
                }

                // сохраняем параметры по имени в map для удобства поиска и фильтрации
                let values = paramsMap.get(o.params[i].name);
                if (!values) {
                    values = new Set<string>();
                    paramsMap.set(o.params[i].name, values);
                }
                values.add(o.params[i].value);

                // ищем фильтр по имени параметра
                const filter = filters.get(o.params[i].name);

                // приводим значия фор и тоталов к числу
                // и записываем в качестве sortOrder
                // исходя из структуры данных: значения фор и тоталов всегда хранятся в первом параметре исхода
                // а sortOrder такого параметра всегда по умолчанию равен 0
                let sortOrder = o.params[i].sortOrder;
                let filterKind = o.params[i].kind;
                if (hasNumericValueParam) {
                    if(i === 0) {
                        sortOrder = parseFloat(o.params[i].value.replace(',', '.'));
                        filterKind = 'NUMERIC_PARAM_VALUE';
                    }
                }

                if (hasNumericValueParam) {
                    if (filterKind === 'NUMERIC_PARAM_VALUE') {
                        numericValueForIndex = o.params[i].value;
                    } else {
                        groupingPairsForIndex.push([o.params[i].name, o.params[i].value]);
                    }
                }

                // если фильтр уже существует, добавляем значение
                if(filter) {
                    filter.valuesMap.set(o.params[i].value, {
                        value: o.params[i].value,
                        sortOrder: sortOrder,
                    });
                    filter.values.push(o.params[i].value);
                    continue;
                }

                // если фильтр не существует, создаем новый
                // и добавляем значение
                // TODO заменить на факторку
                filters.set(o.params[i].name, {
                    name: o.params[i].name,
                    kind: filterKind,
                    valuesMap: new Map().set(o.params[i].value, {
                        value: o.params[i].value,
                        sortOrder: sortOrder,
                    }),
                    values: [o.params[i].value],
                    activeValue: ref<string | undefined>(undefined),
                });
            }

            outcomesToDisplay.push({
                outcome: o,
                name: outcomeName,
                paramsMap,
            });

            // регистрируем outcome в cross-индексе только если у нас есть значение тотала
            if (hasNumericValueParam && numericValueForIndex !== undefined) {
                const groupKey = buildGroupKey(groupingPairsForIndex);
                let numericSet = numericValuesByGroupKey.get(groupKey);
                if (!numericSet) {
                    numericSet = new Set<OutcomeParameterValue>();
                    numericValuesByGroupKey.set(groupKey, numericSet);
                }
                numericSet.add(numericValueForIndex);
            }
        }

        const sortedFilters = sortFiltersValues(filters, market.kind);

        outcomesCtidsByMarketId.set(market.id, outcomesCtids);
        outcomesByMarketId.set(market.id, outcomesToDisplay);
        filtersByMarketId.set(market.id, sortedFilters);
        if (hasNumericValueParam) {
            numericValuesIndexByMarketId.set(market.id, numericValuesByGroupKey);
        }
    }

    return {
        outcomesByMarketId,
        filtersByMarketId,
        outcomesCtidsByMarketId,
        numericValuesIndexByMarketId,
    };
}
